import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = API_BASE;

const PermissionContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {const { t } = useTranslation("common");
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState({
    allowed: [],
    denied: [],
    profiles: [],
    loading: true,
    error: null
  });

  // Fetch user permissions from API
  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setPermissions({ allowed: [], denied: [], profiles: [], loading: false, error: null });
      return;
    }

    try {
      const token = localStorage.getItem('ezerpToken');
      const response = await fetch(`${API_URL}/api/access-profiles/users/${user.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPermissions({
          allowed: data.allowed || [],
          denied: data.denied || [],
          profiles: data.profiles || [],
          loading: false,
          error: null
        });
      } else {
        // If API fails, use role-based fallback
        setPermissions({
          allowed: getRoleFallbackPermissions(user.role),
          denied: [],
          profiles: [user.role],
          loading: false,
          error: null
        });
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // Fallback to role-based permissions
      setPermissions({
        allowed: getRoleFallbackPermissions(user.role),
        denied: [],
        profiles: [user.role],
        loading: false,
        error: error.message
      });
    }
  }, [isAuthenticated, user?.id, user?.role]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((capabilityCode) => {
    // Admin has all permissions
    if (user?.role === 'admin') return true;

    // Check if explicitly denied
    if (permissions.denied.includes(capabilityCode)) return false;

    // Check if explicitly allowed
    if (permissions.allowed.includes(capabilityCode)) return true;

    // Check with wildcards (e.g., "wms.*" allows "wms.inventory.view")
    for (const allowed of permissions.allowed) {
      if (allowed.endsWith('.*')) {
        const prefix = allowed.slice(0, -2);
        if (capabilityCode.startsWith(prefix + '.')) return true;
      }
    }

    // Fallback to role-based check
    return checkRoleFallback(user?.role, capabilityCode);
  }, [user?.role, permissions.allowed, permissions.denied]);

  // Check if user has ANY of the specified permissions
  const hasAnyPermission = useCallback((capabilityCodes) => {
    return capabilityCodes.some((code) => hasPermission(code));
  }, [hasPermission]);

  // Check if user has ALL of the specified permissions
  const hasAllPermissions = useCallback((capabilityCodes) => {
    return capabilityCodes.every((code) => hasPermission(code));
  }, [hasPermission]);

  // Get all permissions for a module
  const getModulePermissions = useCallback((moduleName) => {
    const prefix = `${moduleName}.`;
    return {
      allowed: permissions.allowed.filter((p) => p.startsWith(prefix)),
      denied: permissions.denied.filter((p) => p.startsWith(prefix))
    };
  }, [permissions.allowed, permissions.denied]);

  // Check if user can access a route
  const canAccessRoute = useCallback((route) => {
    const routePermissionMap = {
      // WMS routes
      '/wms/dashboard': ['wms.dashboard.view'],
      '/wms/inventory': ['wms.inventory.view'],
      '/wms/products': ['wms.inventory.view'],
      '/wms/receiving': ['wms.receiving.view'],
      '/wms/putaway': ['wms.putaway.view'],
      '/wms/picking': ['wms.picking.view'],
      '/wms/packing': ['wms.packing.view'],
      '/wms/pallets': ['wms.shipping.view'],
      '/wms/expedition': ['wms.shipping.view'],
      '/wms/returns': ['wms.returns.view'],
      '/wms/config': ['wms.config.view'],
      '/wms/reports': ['wms.dashboard.view'],
      '/wms/geofencing': ['geofencing.dashboard.view'],
      '/wms/coldchain': ['coldchain.dashboard.view'],
      '/wms/trails': ['trails.dashboard.view'],

      // Admin routes
      '/admin/access-profiles': ['admin.profiles.view'],
      '/admin/system-settings': ['admin.system.view'],
      '/admin/api-keys': ['admin.system.edit'],
      '/admin/help-center': ['admin.system.view'],
      '/admin/help': ['admin.system.view'],

      // Finance routes
      '/finance/dashboard': ['finance.dashboard.view'],
      '/finance/general-ledger': ['finance.ledger.view'],
      '/finance/accounts-payable': ['finance.payable.view'],
      '/finance/accounts-receivable': ['finance.receivable.view'],

      // Procurement routes
      '/procurement/dashboard': ['procurement.dashboard.view'],
      '/procurement/vendors': ['procurement.vendors.view'],
      '/procurement/purchase-orders': ['procurement.po.view'],

      // CRM routes
      '/crm/dashboard': ['crm.dashboard.view'],
      '/crm/leads': ['crm.leads.view'],
      '/crm/pipeline': ['crm.pipeline.view'],
      '/crm/opportunities': ['crm.opportunities.view'],
      '/crm/quotes': ['crm.quotes.view'],
      '/crm/accounts': ['crm.accounts.view'],
      '/crm/prospect-discovery': ['crm.prospect_discovery.view'],
      '/crm/campaign-intelligence': ['crm.campaign_intelligence.view'],
      '/crm/marketing-hub': ['crm.marketing_hub.view'],
      '/crm/portal-manager': ['crm.portal_manager.view'],
      '/crm/orchestration': ['crm.orchestration.view'],
      '/crm/publishing-console': ['crm.publishing_console.view'],
      '/crm/ad-connectors': ['crm.ad_connectors.view'],
      '/crm/presentation-hub': ['crm.presentation_hub.view'],
      '/crm/dropship': ['crm.dropship.view'],
      '/crm/consignment': ['crm.consignment.view'],
      '/crm/activities': ['crm.activities.view'],
      '/crm/reports': ['crm.reports.view'],

      // Human Resources routes
      '/hr/dashboard': ['hr.dashboard.view'],
      '/hr/employees': ['hr.employees.view'],
      '/hr/time-attendance': ['hr.time_attendance.view'],
      '/hr/leave': ['hr.leave.view'],
      '/hr/payroll': ['hr.payroll.view'],
      '/hr/pay-components': ['hr.pay_components.view'],
      '/hr/shifts': ['hr.shifts.view'],
      '/hr/positions': ['hr.positions.view'],

      // Accounting routes
      '/accounting/dashboard': ['accounting.dashboard.view'],
      '/accounting/chart-of-accounts': ['accounting.chart_of_accounts.view'],
      '/accounting/journals': ['accounting.journals.view'],
      '/accounting/events': ['accounting.events.view'],
      '/accounting/periods': ['accounting.periods.view'],
      '/accounting/exports': ['accounting.exports.view'],
      '/accounting/posting-rules': ['accounting.posting_rules.view'],

      // Fiscal routes
      '/fiscal/dashboard': ['fiscal.dashboard.view'],
      '/fiscal/documents': ['fiscal.documents.view'],
      '/fiscal/apuracao': ['fiscal.apuracao.view'],
      '/fiscal/sped': ['fiscal.sped.view'],
      '/fiscal/matrix': ['fiscal.matrix.view'],
      '/fiscal/events': ['fiscal.events.view'],
      '/fiscal/audit': ['fiscal.audit.view'],

      // EzParcel routes
      '/ezparcel/dashboard': ['ezparcel.dashboard.view'],
      '/ezparcel/shipments': ['ezparcel.shipments.view'],
      '/ezparcel/routes': ['ezparcel.routes.view'],
      '/ezparcel/drivers': ['ezparcel.drivers.view'],

      // Driver routes
      '/driver': ['driver.dashboard.view'],
      '/driver/route-load': ['driver.route.start'],
      '/driver/route-active': ['driver.route.start'],
      '/driver/stop-deliver': ['driver.stop.deliver'],

      // Mobile routes
      '/mobile': ['mobile.dashboard.view'],
      '/mobile/receiving': ['mobile.receiving.execute'],
      '/mobile/picking': ['mobile.picking.execute'],
      '/mobile/packing': ['mobile.packing.execute']
    };

    // Find matching route
    const routeKey = Object.keys(routePermissionMap).find((key) =>
    route === key || route.startsWith(key + '/')
    );

    if (!routeKey) return true; // No restriction defined = allowed

    const requiredPermissions = routePermissionMap[routeKey];
    return hasAnyPermission(requiredPermissions);
  }, [hasAnyPermission]);

  // Refresh permissions
  const refreshPermissions = useCallback(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const value = {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getModulePermissions,
    canAccessRoute,
    refreshPermissions,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager' || user?.role === 'admin',
    isOperator: ['operator', 'manager', 'admin'].includes(user?.role),
    loading: permissions.loading
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>);

};

// Role-based fallback permissions
function getRoleFallbackPermissions(role) {
  const viewPermissions = [
  'wms.dashboard.view', 'wms.inventory.view', 'wms.receiving.view',
  'wms.picking.view', 'wms.packing.view', 'wms.shipping.view',
  'finance.dashboard.view', 'procurement.dashboard.view',
  'crm.dashboard.view', 'crm.leads.view', 'crm.pipeline.view', 'crm.opportunities.view',
  'crm.quotes.view', 'crm.accounts.view', 'crm.prospect_discovery.view',
  'crm.campaign_intelligence.view', 'crm.marketing_hub.view', 'crm.portal_manager.view',
  'crm.orchestration.view', 'crm.publishing_console.view', 'crm.ad_connectors.view',
  'crm.presentation_hub.view', 'crm.dropship.view', 'crm.consignment.view',
  'crm.activities.view', 'crm.reports.view',
      'hr.dashboard.view', 'hr.employees.view', 'hr.time_attendance.view',
      'hr.leave.view', 'hr.payroll.view', 'hr.pay_components.view',
      'hr.shifts.view', 'hr.positions.view',
      'accounting.dashboard.view', 'accounting.chart_of_accounts.view',
      'accounting.journals.view', 'accounting.events.view', 'accounting.periods.view',
      'accounting.exports.view', 'accounting.posting_rules.view',
      'fiscal.dashboard.view', 'fiscal.documents.view', 'fiscal.apuracao.view',
      'fiscal.sped.view', 'fiscal.matrix.view', 'fiscal.events.view', 'fiscal.audit.view',
  'ezparcel.dashboard.view', 'ezparcel.shipments.view',
  'trails.dashboard.view', 'geofencing.dashboard.view', 'coldchain.dashboard.view'];


  const executePermissions = [
  'wms.receiving.execute', 'wms.putaway.execute', 'wms.picking.execute',
  'wms.packing.execute', 'wms.shipping.execute',
  'driver.route.start', 'driver.stop.deliver', 'driver.pod.signature',
  'mobile.receiving.execute', 'mobile.picking.execute', 'mobile.packing.execute',
  'fiscal.documents.execute', 'fiscal.apuracao.execute', 'fiscal.sped.execute'];


  const managePermissions = [
  'wms.inventory.edit', 'wms.inventory.adjust', 'wms.config.view',
  'ezparcel.shipments.create', 'ezparcel.routes.create', 'ezparcel.routes.assign',
  'finance.ledger.view', 'finance.payable.view', 'finance.receivable.view',
  'procurement.vendors.view', 'procurement.po.view', 'procurement.po.create'];


  const adminPermissions = [
  'admin.users.view', 'admin.users.create', 'admin.users.edit',
  'admin.profiles.view', 'admin.profiles.manage',
  'admin.system.view', 'admin.system.edit', 'admin.logs.view',
  'wms.config.edit', 'finance.payable.approve', 'procurement.po.approve'];


  switch (role) {
    case 'admin':
      return [...viewPermissions, ...executePermissions, ...managePermissions, ...adminPermissions];
    case 'manager':
      return [...viewPermissions, ...executePermissions, ...managePermissions];
    case 'operator':
      return [...viewPermissions, ...executePermissions];
    case 'viewer':
    default:
      return viewPermissions;
  }
}

function checkRoleFallback(role, capabilityCode) {
  if (!role) return false;

  // Admin has all
  if (role === 'admin') return true;

  // Check by capability pattern
  const isView = capabilityCode.includes('.view') || capabilityCode.includes('.dashboard');
  const isExecute = capabilityCode.includes('.execute');
  const isManage = capabilityCode.includes('.edit') || capabilityCode.includes('.create') || capabilityCode.includes('.manage');
  const isAdmin = capabilityCode.startsWith('admin.');
  const isApprove = capabilityCode.includes('.approve');

  switch (role) {
    case 'manager':
      return !isAdmin || !isApprove;
    case 'operator':
      return isView || isExecute;
    case 'viewer':
      return isView;
    default:
      return false;
  }
}

export default PermissionContext;
